<template>
  <div ref="tableContainer" class="payroll-advances-table">
    <DataTable
      ref="dataTableRef"
      data-testid="payroll-advances-table"
      display-mode="handle_user_device"
      :config="tableConfig"
      :data="enrichedAdvances"
      :auto-load="false"
      :refreshButton="{ show: true }"
      @edit-click="handleEditClick"
      @button-click="handleButtonClick"
      @refresh="$emit('refresh')"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { useDateFormatter } from '../../../composables/useDateFormatter';
import { log } from '../../../utils/DebugLogger';
import { ADVANCE_STATUS } from '../../../composables/useEmployeeAdvances';

type Id = number | string;

interface Advance {
  id: Id;
  status: number | string;
  amount: number | string;
  amount_paid?: number | string;
  request_date?: Date | string | null;
  created_at?: Date | string | null;
  employee_name?: string;
  title?: string;
  [key: string]: unknown;
}

interface EnrichedAdvance extends Advance {
  status_display: string;
  status_severity: string;
  request_date_parsed: Date | null;
  created_at_parsed: Date | null;
  amount_remaining: number;
  amount_display: number;
  amount_paid_display: number;
}

interface CellParams {
  data: Record<string, unknown>;
  value?: unknown;
}

interface ActionRow {
  status: number | string;
  [key: string]: unknown;
}

interface Props {
  advances?: Record<string, unknown>[];
  isLoading?: boolean;
  canEdit?: boolean;
  canDelete?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  advances: () => [],
  isLoading: false,
  canEdit: true,
  canDelete: true
});

const emit = defineEmits<{
  'edit': [advance: EnrichedAdvance | { id: Id }];
  'view': [advance: EnrichedAdvance | { id: Id }];
  'cancel': [advance: EnrichedAdvance | { id: Id }];
  'reopen': [advance: EnrichedAdvance | { id: Id }];
  'delete': [advance: EnrichedAdvance | { id: Id }];
  'refresh': [];
}>();

const { t, n } = useI18n();
const { formatDate: formatDateLocale, formatDateTime: formatDateTimeLocale, fromApiDateToDate } = useDateFormatter();

// Refs
const dataTableRef = ref<unknown>(null);
const tableContainer = ref<HTMLElement | null>(null);

// Computed: Enriched advances data
const enrichedAdvances = computed<EnrichedAdvance[]>(() => {
  return (props.advances as Advance[]).map(advance => {
    // Status display
    const statusDisplay = getStatusDisplay(advance.status);

    // Parse dates
    // NOTE: request_date uses fromApiDateToDate (noon local) to avoid timezone issues
    const requestDate = advance.request_date instanceof Date
      ? advance.request_date
      : advance.request_date ? fromApiDateToDate(advance.request_date as string) : null;

    const createdAt = advance.created_at instanceof Date
      ? advance.created_at
      : advance.created_at ? new Date(advance.created_at as string) : null;

    return {
      ...advance,
      status_display: statusDisplay.label,
      status_severity: statusDisplay.severity,
      request_date_parsed: requestDate,
      created_at_parsed: createdAt,
      // Amount is stored in reais (decimal) - no conversion needed
      amount_remaining: parseFloat(String(advance.amount)) - parseFloat(String(advance.amount_paid || 0)),
      amount_display: parseFloat(String(advance.amount)) || 0,
      amount_paid_display: parseFloat(String(advance.amount_paid || 0)) || 0
    };
  });
});

const getStatusDisplay = (status: number | string): { label: string; severity: string } => {
  switch (parseInt(String(status))) {
    case ADVANCE_STATUS.CANCELED:
      return { label: t('Cancelado'), severity: 'danger' };
    case ADVANCE_STATUS.OPEN:
      return { label: t('Aberto'), severity: 'warning' };
    case ADVANCE_STATUS.PARTIAL:
      return { label: t('Parcial'), severity: 'info' };
    case ADVANCE_STATUS.PAID:
      return { label: t('Pago'), severity: 'success' };
    default:
      return { label: t('Desconhecido'), severity: 'secondary' };
  }
};

// Render functions
const renderStatus = ({ value, data }: CellParams): string => {
  const row = data as { status_severity?: string };
  const severity = row.status_severity || 'secondary';
  return `<span class="p-tag p-tag-${severity}">${value}</span>`;
};

const renderAmount = ({ data }: CellParams): string => {
  const row = data as { amount_display?: number };
  const amount = row.amount_display || 0;
  return `<span class="amount-value">${n(amount, 'currency')}</span>`;
};

const renderAmountRemaining = ({ data }: CellParams): string => {
  const row = data as { amount_remaining?: number };
  const remaining = row.amount_remaining || 0;
  const color = remaining > 0 ? 'var(--orange-600)' : 'var(--green-600)';
  return `<span class="amount-value" style="color: ${color}">${n(remaining, 'currency')}</span>`;
};

const renderDate = ({ data }: CellParams): string => {
  const row = data as { request_date_parsed?: Date | null };
  const date = row.request_date_parsed;
  if (!date || isNaN(date.getTime())) {
    return `<span class="text-muted">-</span>`;
  }
  return formatDateLocale(date);
};

const renderCreatedAt = ({ data }: CellParams): string => {
  const row = data as { created_at_parsed?: Date | null };
  const date = row.created_at_parsed;
  if (!date || isNaN(date.getTime())) {
    return `<span class="text-muted">-</span>`;
  }
  return formatDateTimeLocale(date);
};

// Status filter options
const statusFilterOptions = computed(() => [
  { label: t('Aberto'), value: t('Aberto') },
  { label: t('Parcial'), value: t('Parcial') },
  { label: t('Pago'), value: t('Pago') },
  { label: t('Cancelado'), value: t('Cancelado') }
]);

// Table configuration
const tableConfig = computed(() => {
  const columns: Record<string, unknown>[] = [
    {
      data: 'id',
      title: t('ID'),
      renderType: 'clickable_id',
      minWidth: 70,
      sortable: true
    },
    {
      data: 'title',
      title: t('Título'),
      minWidth: 180,
      sortable: true
    },
    {
      data: 'amount_display',
      title: t('Valor Total'),
      minWidth: 110,
      sortable: true,
      cellRenderer: renderAmount
    },
    {
      data: 'amount_remaining',
      title: t('Restante'),
      minWidth: 110,
      sortable: true,
      cellRenderer: renderAmountRemaining
    },
    {
      data: 'request_date_parsed',
      title: t('Data Solicitação'),
      minWidth: 130,
      sortable: true,
      cellRenderer: renderDate
    },
    {
      data: 'status_display',
      title: t('Status'),
      minWidth: 100,
      sortable: true,
      filterType: 'select',
      filterOptions: statusFilterOptions.value,
      cellRenderer: renderStatus
    }
  ];

  // Add user column (employee_name comes from backend via Cognito)
  columns.splice(2, 0, {
    data: 'employee_name',
    title: t('Colaborador'),
    minWidth: 150,
    sortable: true,
    render: (data: string | null | undefined): string => data || '-'
  });

  // Add created_at column
  columns.push({
    data: 'created_at_parsed',
    title: t('Criado em'),
    minWidth: 140,
    sortable: true,
    cellRenderer: renderCreatedAt
  });

  // Build actions array
  const actions: Array<{
    type: string;
    label: string;
    icon: string;
    severity?: string;
    showCondition?: (row: ActionRow) => boolean;
  }> = [];

  // Cancel button (for OPEN and PARTIAL)
  actions.push({
    type: 'cancel',
    label: t('Cancelar'),
    icon: 'pi pi-times',
    severity: 'danger',
    showCondition: (row: ActionRow) => {
      const status = parseInt(String(row.status));
      return status === ADVANCE_STATUS.OPEN || status === ADVANCE_STATUS.PARTIAL;
    }
  });

  // Reopen button (for CANCELED only)
  actions.push({
    type: 'reopen',
    label: t('Reabrir'),
    icon: 'pi pi-undo',
    severity: 'warning',
    showCondition: (row: ActionRow) => parseInt(String(row.status)) === ADVANCE_STATUS.CANCELED
  });

  // Delete button (for OPEN and CANCELED)
  if (props.canDelete) {
    actions.push({
      type: 'delete',
      label: t('Excluir'),
      icon: 'pi pi-trash',
      severity: 'danger',
      showCondition: (row: ActionRow) => {
        const status = parseInt(String(row.status));
        return status === ADVANCE_STATUS.OPEN || status === ADVANCE_STATUS.CANCELED;
      }
    });
  }

  // View button
  actions.push({
    type: 'view',
    label: t('Ver'),
    icon: 'pi pi-eye'
  });

  // Add actions column
  if (actions.length > 0) {
    columns.push({
      data: null,
      title: t('Ações'),
      minWidth: 180,
      sortable: false,
      renderType: 'action_buttons',
      buttons: actions.map(action => ({
        text: action.label,
        icon: action.icon,
        action: action.type,
        class: action.severity ? `p-button-${action.severity}` : '',
        condition: action.showCondition
      }))
    });
  }

  return {
    columns,
    actions,
    emptyMessage: t('Nenhum adiantamento encontrado'),
    paginator: true,
    rows: 10,
    rowsPerPageOptions: [5, 10, 25, 50],
    rowTestId: (data: Record<string, unknown>) => {
      const id = data?.id;
      return id == null ? '' : `advances-row-${id}`;
    }
  };
});

// Event handlers
const handleEditClick = (value: Id | EnrichedAdvance): void => {
  log('EmployeeAdvancesTable', 'handleEditClick', 'Edit clicked', value);

  // Find the full row from enrichedAdvances
  let advance: EnrichedAdvance | null = null;

  if (typeof value === 'number' || typeof value === 'string') {
    advance = enrichedAdvances.value.find(r => r.id === parseInt(String(value)) || r.id === value) ?? null;
    log('EmployeeAdvancesTable', 'handleEditClick', 'Found advance by ID', { id: value, advance });
  } else if (value && typeof value === 'object') {
    advance = value as EnrichedAdvance;
  }

  if (advance) {
    emit('edit', advance);
  } else {
    log('EmployeeAdvancesTable', 'handleEditClick', 'Advance not found, emitting ID only', value);
    emit('edit', { id: value as Id });
  }
};

interface ButtonClickPayload {
  action: string;
  data: EnrichedAdvance;
}

const handleButtonClick = ({ action, data }: ButtonClickPayload): void => {
  log('EmployeeAdvancesTable', 'handleButtonClick', 'Button clicked', { action, data });

  switch (action) {
    case 'edit':
      emit('edit', data);
      break;
    case 'view':
      emit('view', data);
      break;
    case 'cancel':
      emit('cancel', data);
      break;
    case 'reopen':
      emit('reopen', data);
      break;
    case 'delete':
      emit('delete', data);
      break;
  }
};

// Lifecycle
onMounted(() => {
  log('EmployeeAdvancesTable', 'onMounted', 'Component mounted');
});

onBeforeUnmount(() => {
  log('EmployeeAdvancesTable', 'onBeforeUnmount', 'Component unmounted');
});
</script>

<style scoped>
.payroll-advances-table {
  width: 100%;
}

:deep(.amount-value) {
  font-weight: 600;
  color: var(--primary-600);
}

:deep(.text-muted) {
  color: var(--text-color-secondary);
}
</style>
